import { isApiFailure, type ApiResponse } from '@seenaly/shared';

/**
 * Thin fetch wrapper over the API envelope (docs/spec.md §5).
 *
 * `credentials: 'include'` is required from P2 onward — auth is an httpOnly
 * session cookie, and in production the SPA and API sit behind the same origin
 * via Caddy.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
    /** Extra payload some errors carry, e.g. the clashing appointments on a 409. */
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError('INTERNAL', `Non-JSON response from ${path} (HTTP ${res.status})`);
  }

  if (isApiFailure(body)) {
    // Some errors attach domain data alongside the envelope — the booking
    // conflict, for instance, ships the appointments it clashes with so the
    // dialog can show them.
    const { code, message, fields, ...detail } = body.error as ApiFailureError;

    // A session that dies mid-shift should send the user to the login screen,
    // not surface as a generic failure on whatever screen they were using.
    // `/auth/me` is exempt: it answers 401 for every anonymous visitor on boot,
    // which is normal, not an expiry.
    if (code === 'UNAUTHENTICATED' && path !== '/auth/me') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    throw new ApiError(code, message, fields, Object.keys(detail).length ? detail : undefined);
  }

  return body.data;
}

/** Fired when the API rejects a request because the session is gone. */
export const SESSION_EXPIRED_EVENT = 'sn:session-expired';

type ApiFailureError = {
  code: string;
  message: string;
  fields?: Record<string, string>;
} & Record<string, unknown>;

/** Convenience wrappers so call sites read as verbs. */
export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

/** Builds a query string, dropping empty values. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}
